const SUIT_VALUE = {
	C: 1,
	D: 2,
	H: 3,
	S: 4,
	NT: 5,
};

/**
 * An instance of a bid
 * @param  {String} bidStr the string representing the bid, e.g. `1S` or `X`, or `P`, or `7NT`, or `XX`
 * @param  {Player} player the player making the bid
 * @return {Bid}        [description]
 */
class Bid {
	constructor(bidStr, player) {
		this.player = player;

		if (!this.validBid(bidStr)) {
			throw new Error(`${bidStr} is not a valid bid`);
		}
		this.setBid(bidStr);
	}

	/**
	 * @private
	 * @param {String} bid - validates and sets the bid
	 */
	setBid(bidStr) {
		this.bid = bidStr;
	}

	/**
	 * The name of this bid
	 * @return {String} a string representing this bid e.g. 2H, 1NT, P, X, XX etc.
	 */
	get name() {
		return this.bid;
	}

	/**
	 * Get the value if this is a contract bid
	 * @return {String} value of bid a number between 1 and 7 as a string
	 */
	get value() {
		if (this.isContractBid) {
			return this.bid[0];
		}
		return null;
	}

	/**
	 * Get the suit in short format, if this is a contract bid
	 * @return {String} suit of bid one of `C,D,H,S,NT`.
	 */
	get suit() {
		if (['P', 'X', 'XX'].includes(this.bid)) return null;
		return this.bid.slice(1);
	}

	/**
	 * Whether this bid was a contract bid e.g. 1NT or 2H
	 * @return {Boolean} true if this was a contract bid
	 */
	get isContractBid() {
		return !['P', 'X', 'XX'].includes(this.bid);
	}
	/**
	 * Whether this was a pass bid
	 * @return {Boolean} true if this bid was a pass `P``
	 */
	get isPass() {
		return this.bid == 'P';
	}

	/**
	 * Whether this was a Double bid
	 * @return {Boolean} true if this is a double `X`
	 */
	get isDouble() {
		return this.bid == 'X';
	}

	/**
	 * Whether this is a Redouble bid
	 * @return {Boolean} true if bid is `XX`
	 */
	get isReDouble() {
		return this.bid == 'XX';
	}

	/**
	 * User for comparing bid equivelance, not for display
	 * @example
	 * let bid1 = new Bid("2C");
	 * let bid2 = new Bid("4H");
	 *
	 * bid2 > bid1; true
	 * @return {Int} a number generated based on value and suit
	 */
	get bidValue() {
		if (!this.isContractBid) {
			return false;
		}

		return Number(this.bid[0]) * 10 + SUIT_VALUE[this.bid.slice(1)];
	}

	/**
	 * Simple validation of bid syntax
	 * @param  {string} bid string to validate
	 * @return {boolean}  true if bid is valid
	 */
	validBid(bid) {
		// one of P, X, XX
		// in form 1-7 + CDHS NT
		if (['P', 'X', 'XX'].includes(bid)) {
			return true;
		}

		if (['1', '2', '3', '4', '5', '6', '7'].includes(bid[0])) {
			if (['C', 'H', 'D', 'S', 'NT'].includes(bid.slice(1))) {
				return true;
			}
		}

		return false;
	}
}

module.exports = Bid;
